import type { Place } from '@/hooks/usePlaces';
import { translations, type Language } from '@/translations';

/** Ratio of actual route distance to straight-line distance. */
export const ROAD_FACTORS: Record<string, number> = { walking: 2.5, transit: 2.0, taxi: 1.8 };

/** Effective pace in metres per minute after the road factor is applied. */
export const PACE_M_PER_MIN: Record<string, number> = { walking: 80, transit: 250, taxi: 400 };

export function travelStrings(language: string) {
  return (translations[language as Language] ?? translations.en).travel;
}

/** One-way minutes from a straight-line distance. Same numbers at trip
 *  creation, in the offline ranker, in the prompt context and in Tune. */
export function estimateTravelMins(straightLineMeters: number, transport: string): number {
  const road = straightLineMeters * (ROAD_FACTORS[transport] ?? 2.0);
  const pace = PACE_M_PER_MIN[transport] ?? 80;
  return Math.max(1, Math.round(road / pace));
}

/** "12 min walk" / "~6 min by transit" / "~4 min drive", in the user's language. */
export function formatDistanceNote(travelMins: number, transport: string, language: string): string {
  const t = travelStrings(language);
  if (transport === 'walking') return t.minWalk.replace('{n}', String(travelMins));
  const n = String(Math.max(travelMins, 2));
  return (transport === 'transit' ? t.minTransit : t.minDrive).replace('{n}', n);
}

export function formatTravelText(mins: number, language: string): string {
  const t = travelStrings(language);
  if (mins < 60) return t.mins.replace('{n}', String(mins));
  return t.hrMins.replace('{h}', String(Math.floor(mins / 60))).replace('{m}', String(mins % 60));
}

/** Google routing durations, when the search returned them, keyed by place id. */
export function buildRealTravelTimes(places: Place[], language: string): Map<string, { mins: number; text: string }> {
  const map = new Map<string, { mins: number; text: string }>();
  for (const p of places) {
    if (p.routingDurationSeconds) {
      const mins = Math.round(p.routingDurationSeconds / 60);
      map.set(p.id, { mins, text: formatTravelText(mins, language) });
    }
  }
  return map;
}

/** Distance label when no routing data exists: straight-line estimate. */
export function estimateDistanceNote(place: Place, transport: string, language: string): string {
  const t = travelStrings(language);
  if (!place.distanceMeters) return t.nearby;
  if (place.distanceMeters < 300) return t.rightHere;
  return formatDistanceNote(estimateTravelMins(place.distanceMeters, transport), transport, language);
}
